import React from "react";
import { Box, Typography, Pagination, Select, MenuItem, Stack } from "@mui/material";

/**
 * CommonPagination - Reusable pagination component for all tables/lists across the application.
 *
 * @param {number} page - Current page number (1-based index)
 * @param {number} count - Total number of pages (can also be calculated if totalItems & rowsPerPage provided)
 * @param {number} totalItems - Total count of records
 * @param {number} rowsPerPage - Number of items displayed per page (default: 10)
 * @param {function} onPageChange - Callback when page changes: (newPage) => void
 * @param {function} onRowsPerPageChange - (Optional) Callback when rowsPerPage changes: (newRowsPerPage) => void
 * @param {Array<number>} rowsPerPageOptions - (Optional) Array of row size options, e.g. [10, 25, 50]
 * @param {string} itemName - Entity label for the summary text, e.g. "departments", "records"
 * @param {boolean} showSummary - Whether to display the "Showing X to Y of Z" summary
 * @param {object} sx - Custom container style overrides
 */
const CommonPagination = ({
  page = 1,
  count,
  totalItems,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions,
  itemName = "records",
  showSummary = true,
  sx = {},
}) => {
  // Determine total pages
  const calculatedCount =
    count !== undefined && count !== null
      ? count
      : totalItems !== undefined && totalItems !== null
        ? Math.ceil(totalItems / rowsPerPage) || 1
        : 1;

  // If there are literally 0 items, don't show pagination bar
  if (totalItems !== undefined && totalItems !== null && totalItems <= 0) {
    return null;
  }

  // Calculate summary bounds
  const startItem = totalItems !== undefined ? Math.min((page - 1) * rowsPerPage + 1, totalItems) : (page - 1) * rowsPerPage + 1;
  const endItem = totalItems !== undefined ? Math.min(page * rowsPerPage, totalItems) : page * rowsPerPage;

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderTop: "1px solid #f0f0f0",
        bgcolor: "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        ...sx,
      }}
    >
      {/* Left side: Summary & Rows per page */}
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        {showSummary && (
          <Typography variant="body2" color="text.secondary" fontWeight={500} fontSize={13}>
            {totalItems !== undefined ? (
              <>
                Showing <strong>{startItem}</strong> to <strong>{endItem}</strong> of <strong>{totalItems}</strong> {itemName}
              </>
            ) : (
              <>
                Page <strong>{page}</strong> of <strong>{calculatedCount}</strong>
              </>
            )}
          </Typography>
        )}

        {onRowsPerPageChange && rowsPerPageOptions && rowsPerPageOptions.length > 0 && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary" fontSize={13}>
              Rows per page:
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                onRowsPerPageChange(Number(e.target.value));
              }}
              sx={{
                fontSize: 13,
                height: 30,
                bgcolor: "#fff",
                "& fieldset": { borderColor: "#e0e0e0" },
                "&.Mui-focused fieldset": { borderColor: "#D20000" },
              }}
            >
              {rowsPerPageOptions.map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
      </Stack>

      {/* Right side: Pagination buttons */}
      <Pagination
        count={Math.max(1, calculatedCount)}
        page={Math.max(1, page)}
        onChange={(_, val) => {
          if (onPageChange && val !== page) {
            onPageChange(val);
          }
        }}
        color="primary"
        showFirstButton
        showLastButton
        size="medium"
        sx={{
          "& .MuiPaginationItem-root": {
            fontSize: "13px",
            fontWeight: 600,
            color: "#4a4a4a",
            borderRadius: "6px",
            minWidth: 32,
            height: 32,
            "&:hover": {
              bgcolor: "#f5f5f5",
            },
          },
          "& .MuiPaginationItem-root.Mui-selected": {
            bgcolor: "#D20000 !important",
            color: "#fff !important",
            "&:hover": {
              bgcolor: "#a80000 !important",
            },
          },
        }}
      />
    </Box>
  );
};

export default CommonPagination;
